import { jsPDF } from 'jspdf';

/**
 * Feuille d'émargement PDF (Qualiopi) — sprint « émargement » (09/10/2026).
 *
 * Généré côté client avec `jspdf` (déjà dans package.json, aucune dépendance ajoutée).
 * Contenu imposé par la spécification §4 :
 *   1. En-tête logo + « KAYLISS DIGITAL — Feuille d'émargement » + mention Qualiopi
 *   2. Bloc identification de la demi-journée
 *   3. Tableau NOM Prénom | Statut | Signature (image) | Horodatage
 *   4. Pied de page : signature du formateur + conservation 5 ans + date d'édition
 */

export interface AttendancePdfParticipant {
  name: string;
  email?: string;
  status: string;
  signature: string;
  signedAt: string | null;
  method: string;
  comment?: string;
}

export interface AttendancePdfData {
  courseTitle: string;
  sheetTitle: string;
  date: string | Date;
  period: string;
  startTime: string;
  endTime: string;
  modality: string;
  location: string;
  trainerName: string;
  participants: AttendancePdfParticipant[];
}

const STATUS_LABELS: Record<string, string> = {
  present: 'Présent',
  retard: 'En retard',
  absent: 'Absent',
  excuse: 'Excusé'
};

const PERIOD_LABELS: Record<string, string> = {
  matin: 'matin',
  'apres-midi': 'après-midi',
  journee: 'journée'
};

const MODALITY_LABELS: Record<string, string> = {
  presentiel: 'Présentiel',
  distanciel: 'Distanciel',
  mixte: 'Mixte'
};

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 14;
const TABLE_BOTTOM = 252;
// Document officiel : les dates doivent être celles de la formation (heure française),
// jamais celles du fuseau du navigateur qui consulte la fiche.
const TIME_ZONE = 'Europe/Paris';

const parisDayKey = (value: string | Date): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return new Date().toISOString().slice(0, 10);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const formatDateFr = (value: string | Date): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-FR', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatDateTimeFr = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const slug = (value: string): string =>
  (value || 'formation')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .slice(0, 40) || 'formation';

const loadImage = (src: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

/**
 * Rasterise une image en PNG en la réduisant à `maxDimension` px.
 * jsPDF embarque les images en bitmap brut : sans réduction, un logo ou une
 * signature de 1500 px produit un PDF de plusieurs mégaoctets.
 */
const imageToDataUrl = (image: HTMLImageElement, maxDimension = 260): string => {
  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;
  if (!naturalWidth || !naturalHeight) return '';
  const scale = Math.min(1, maxDimension / Math.max(naturalWidth, naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return '';
  }
};

/** Ré-encode un dataURL de signature à une largeur raisonnable pour le PDF. */
const downscaleSignature = (dataUrl: string, maxWidth = 420): Promise<string> =>
  new Promise((resolve) => {
    if (!dataUrl || !dataUrl.startsWith('data:image/')) {
      resolve(dataUrl);
      return;
    }
    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth || maxWidth;
      const height = image.naturalHeight || Math.round(maxWidth / 3);
      const targetWidth = Math.max(1, Math.min(maxWidth, width));
      const targetHeight = Math.max(1, Math.round(targetWidth * (height / (width || 1))));
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(dataUrl);
      }
    };
    image.onerror = () => resolve(dataUrl);
    image.src = dataUrl;
  });

export const exportAttendancePdf = async (data: AttendancePdfData): Promise<string> => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // --- 1. En-tête ---
  const logo = await loadImage('/logo.png');
  if (logo) {
    const logoData = imageToDataUrl(logo, 260);
    if (logoData) {
      doc.addImage(logoData, 'PNG', MARGIN, 9, 17, 17, undefined, 'FAST');
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(0, 51, 102);
  doc.text('KAYLISS DIGITAL — Feuille d\u2019émargement', MARGIN + 21, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(70, 70, 70);
  doc.text('Organisme certifié Qualiopi — Actions de formation', MARGIN + 21, 22.5);

  doc.setDrawColor(0, 102, 204);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, 30, PAGE_WIDTH - MARGIN, 30);

  // --- 2. Bloc identification ---
  const horaires =
    data.startTime || data.endTime ? ` — ${data.startTime || '?'} à ${data.endTime || '?'}` : '';

  const infoLines: Array<[string, string]> = [
    ['Formation', data.courseTitle || '—'],
    ['Demi-journée', data.sheetTitle || '—'],
    ['Date', `${formatDateFr(data.date)} (${PERIOD_LABELS[data.period] || data.period || 'journée'})${horaires}`],
    ['Modalité', MODALITY_LABELS[data.modality] || data.modality || '—'],
    ['Lieu', data.location || '—'],
    ['Formateur', data.trainerName || '—']
  ];

  let cursorY = 38;
  doc.setFontSize(10);
  infoLines.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 35, 71);
    doc.text(`${label} :`, MARGIN, cursorY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(20, 20, 20);
    doc.text(doc.splitTextToSize(String(value), PAGE_WIDTH - MARGIN * 2 - 26)[0] || '—', MARGIN + 26, cursorY);
    cursorY += 6;
  });

  cursorY += 2;

  // --- 3. Tableau ---
  const columns = [
    { key: 'name', label: 'NOM Prénom', x: MARGIN, w: 62 },
    { key: 'status', label: 'Statut', x: MARGIN + 62, w: 26 },
    { key: 'signature', label: 'Signature', x: MARGIN + 88, w: 62 },
    { key: 'signedAt', label: 'Horodatage', x: MARGIN + 150, w: 32 }
  ];
  const ROW_HEIGHT = 17;

  const drawTableHeader = (y: number): number => {
    doc.setFillColor(0, 102, 204);
    doc.rect(MARGIN, y, PAGE_WIDTH - MARGIN * 2, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    columns.forEach((column) => {
      doc.text(column.label, column.x + 2, y + 5.4);
    });
    return y + 8;
  };

  let y = drawTableHeader(cursorY);
  doc.setTextColor(20, 20, 20);

  for (let index = 0; index < data.participants.length; index += 1) {
    const participant = data.participants[index];

    if (y + ROW_HEIGHT > TABLE_BOTTOM) {
      doc.addPage();
      // Reprise d'en-tête + identification sur chaque page (audit Kael §D.3) : un auditeur
      // Qualiopi doit pouvoir identifier la page sans remonter à la page 1.
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0, 51, 102);
      doc.text('KAYLISS DIGITAL \u2014 Feuille d\u2019émargement (suite)', MARGIN, 16);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(70, 70, 70);
      doc.text(
        doc.splitTextToSize(
          `${data.courseTitle || '—'} \u2014 ${formatDateFr(data.date)} (${PERIOD_LABELS[data.period] || data.period || 'journée'}) \u2014 Formateur : ${data.trainerName || '—'}`,
          PAGE_WIDTH - MARGIN * 2
        )[0] || '—',
        MARGIN,
        22.5
      );

      doc.setDrawColor(0, 102, 204);
      doc.setLineWidth(0.6);
      doc.line(MARGIN, 27, PAGE_WIDTH - MARGIN, 27);

      y = drawTableHeader(32);
      doc.setTextColor(20, 20, 20);
    }

    if (index % 2 === 1) {
      doc.setFillColor(246, 249, 252);
      doc.rect(MARGIN, y, PAGE_WIDTH - MARGIN * 2, ROW_HEIGHT, 'F');
    }
    doc.setDrawColor(210, 220, 230);
    doc.setLineWidth(0.2);
    doc.rect(MARGIN, y, PAGE_WIDTH - MARGIN * 2, ROW_HEIGHT);
    columns.slice(1).forEach((column) => {
      doc.line(column.x, y, column.x, y + ROW_HEIGHT);
    });

    // Nom (+ email en second niveau)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const nameLines = doc.splitTextToSize(participant.name || '—', columns[0].w - 4).slice(0, 2);
    doc.text(nameLines, columns[0].x + 2, y + 5.6);

    // Statut
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(STATUS_LABELS[participant.status] || participant.status || '—', columns[1].x + 2, y + 7);

    // Signature (image)
    const signatureDataUrl = await downscaleSignature(participant.signature || '', 420);
    if (signatureDataUrl.startsWith('data:image/')) {
      const format = signatureDataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
      const maxWidth = columns[2].w - 4;
      const maxHeight = ROW_HEIGHT - 4;
      try {
        const props = doc.getImageProperties(signatureDataUrl);
        const ratio = props.width && props.height ? props.width / props.height : 3;
        let drawWidth = maxWidth;
        let drawHeight = drawWidth / ratio;
        if (drawHeight > maxHeight) {
          drawHeight = maxHeight;
          drawWidth = drawHeight * ratio;
        }
        doc.addImage(signatureDataUrl, format, columns[2].x + 2, y + 2, drawWidth, drawHeight, undefined, 'FAST');
      } catch {
        doc.setFontSize(8);
        doc.text('signature illisible', columns[2].x + 2, y + 9);
      }
    } else {
      doc.setFontSize(7.5);
      doc.setTextColor(110, 110, 110);
      const mention =
        participant.method === 'manual'
          ? 'Saisie formateur'
          : participant.signedAt
            ? '—'
            : 'Non signé';
      doc.text(mention, columns[2].x + 2, y + 9);
      doc.setTextColor(20, 20, 20);
    }

    // Horodatage
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(formatDateTimeFr(participant.signedAt), columns[3].x + 2, y + 7);

    y += ROW_HEIGHT;
  }

  if (!data.participants.length) {
    doc.setFontSize(9);
    doc.text('Aucun participant enregistré pour cette demi-journée.', MARGIN + 2, y + 7);
    y += ROW_HEIGHT;
  }

  // --- 4. Pied de page ---
  const footerY = Math.max(y + 10, 258);
  doc.setDrawColor(0, 102, 204);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, footerY - 5, PAGE_WIDTH - MARGIN, footerY - 5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(0, 35, 71);
  doc.text('Signature du formateur', MARGIN, footerY);

  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, footerY + 12, MARGIN + 70, footerY + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  doc.text(`Formateur : ${data.trainerName || '—'}`, MARGIN + 74, footerY + 4);

  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text('Document conservé 5 ans — preuve d\u2019assiduité (Qualiopi)', MARGIN, footerY + 20);
  doc.text(`Édité le ${formatDateFr(new Date())}`, PAGE_WIDTH - MARGIN, footerY + 20, { align: 'right' });

  doc.setFontSize(7);
  doc.text(
    `KAYLISS DIGITAL — feuille d'émargement générée depuis le LMS (${data.participants.length} participant(s))`,
    MARGIN,
    PAGE_HEIGHT - 8
  );

  // Pagination (audit Kael §D.3) : « Page N/N » sur chaque page.
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`Page ${page}/${totalPages}`, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 12, { align: 'right' });
  }

  const filename = `emargement-${slug(data.courseTitle)}-${parisDayKey(data.date)}-${data.period || 'journee'}.pdf`;
  doc.save(filename);
  return filename;
};
